import { useCallback, useEffect, useState } from 'react'
import { adminClient } from '../../api'
import type { AdminBotStatusV3, AdminBotV3Entry } from '../../api/adminTypes'
import { AdminNotice, EmailLookupForm } from './adminShared'
import { describeAdminError, useAdminActions, useEmailParam } from './adminHelpers'
function isRefused(result: unknown): boolean {
  return Array.isArray(result)
}

/**
 * Equivalent of legacy `AdminUserBotStatusComponent` (route
 * `admin/bot/user/status`, contract #22-24). BUSINESS-CRITICAL —
 * start/modify place or change a real bot control request at spotbot for a
 * user who is NOT the admin. Contract rules pinned here:
 *  - spotbot refusing either call answers 200 `[]` — MUST be shown as
 *    "not done", never as success (`isRefused`);
 *  - both share the B4 in-flight guard (409 `bot_action_in_progress`);
 *  - a 502 leaves the outcome UNKNOWN — no auto-retry, ever
 *    (`useAdminActions`'s in-flight ref also blocks a same-tick double
 *    click, same mechanism as B4's `useBotActions`);
 *  - `exchangeKey` on each bot entry is PRESENCE ONLY (`key`/`secret` are
 *    `0`/`1`) — never render a value, there is none to render.
 */
export function AdminUserBotStatusPage() {
  const [email, setEmail] = useEmailParam()
  const [status, setStatus] = useState<AdminBotStatusV3 | null>(null)
  const [loadFeedback, setLoadFeedback] = useState<ReturnType<typeof describeAdminError> | undefined>()
  const { isBusy, feedbackFor, run, setFeedback } = useAdminActions()
  const [modifyDrafts, setModifyDrafts] = useState<Record<string, string>>({})

  // `refresh` re-fetches WITHOUT clearing `status` first — used after a
  // start/modify so the bot cards (and their just-set feedback) stay on
  // screen instead of flashing to "Loading…" and back. `load` (email
  // change / initial mount) resets first, since there is nothing worth
  // keeping on screen yet.
  const refresh = useCallback((targetEmail: string) => {
    setLoadFeedback(undefined)
    adminClient
      .botStatusV3(targetEmail)
      .then((result) => setStatus(result))
      .catch((err) => setLoadFeedback(describeAdminError(err)))
  }, [])
  const load = useCallback(
    (targetEmail: string) => {
      setStatus(null)
      refresh(targetEmail)
    },
    [refresh],
  )

  useEffect(() => {
    if (email) load(email)
    else setStatus(null)
  }, [email, load])

  function start(exchangeId: string) {
    if (!email) return
    void run(
      `start-${exchangeId}`,
      () => adminClient.startBot(exchangeId, email),
      (result) => {
        if (isRefused(result)) {
          setFeedback(`start-${exchangeId}`, { tone: 'warning', text: 'Spotbot refused the start request — the bot was NOT started.' })
        } else if (!('defaultConfig' in (result as Record<string, unknown>))) {
          setFeedback(`start-${exchangeId}`, {
            tone: 'warning',
            text: 'Bot status created, but its default config could not be applied.',
          })
        } else {
          setFeedback(`start-${exchangeId}`, { tone: 'success', text: 'Bot started.' })
        }
        refresh(email)
      },
    )
  }

  function modify(exchangeId: string) {
    if (!email) return
    let body: Record<string, unknown>
    try {
      body = modifyDrafts[exchangeId]?.trim() ? (JSON.parse(modifyDrafts[exchangeId]!) as Record<string, unknown>) : {}
    } catch {
      setFeedback(`modify-${exchangeId}`, { tone: 'error', text: 'Invalid JSON body.' })
      return
    }
    void run(
      `modify-${exchangeId}`,
      () => adminClient.modifyBot(exchangeId, email, body),
      (result) => {
        if (isRefused(result)) {
          setFeedback(`modify-${exchangeId}`, { tone: 'warning', text: 'Spotbot refused the modify request — nothing changed.' })
        } else {
          setFeedback(`modify-${exchangeId}`, { tone: 'success', text: 'Bot updated.' })
        }
        refresh(email)
      },
    )
  }

  function renderBot(bot: AdminBotV3Entry) {
    return (
      <div className="portal-card" key={bot.exchangeId}>
        <h3>{bot.exchangeId}</h3>
        <dl className="portal-kv">
          <dt>Type</dt>
          <dd>{bot.type || 'not set'}</dd>
          <dt>Status</dt>
          <dd>{bot.status}</dd>
          <dt>Exchange key</dt>
          <dd>{bot.exchangeKey.key ? 'set' : 'not set'}</dd>
          <dt>Exchange secret</dt>
          <dd>{bot.exchangeKey.secret ? 'set' : 'not set'}</dd>
        </dl>
        <div className="portal-actions">
          <button type="button" disabled={isBusy(`start-${bot.exchangeId}`)} onClick={() => start(bot.exchangeId)}>
            {isBusy(`start-${bot.exchangeId}`) ? 'Starting…' : 'Start bot'}
          </button>
        </div>
        <AdminNotice feedback={feedbackFor(`start-${bot.exchangeId}`)} />

        <h4>Modify (raw JSON body, forwarded to spotbot verbatim)</h4>
        <textarea
          aria-label={`Modify body for ${bot.exchangeId}`}
          rows={3}
          value={modifyDrafts[bot.exchangeId] ?? ''}
          onChange={(e) => setModifyDrafts((prev) => ({ ...prev, [bot.exchangeId]: e.target.value }))}
          placeholder='{"status": 0}'
        />
        <div className="portal-actions">
          <button type="button" disabled={isBusy(`modify-${bot.exchangeId}`)} onClick={() => modify(bot.exchangeId)}>
            {isBusy(`modify-${bot.exchangeId}`) ? 'Saving…' : 'Modify bot'}
          </button>
        </div>
        <AdminNotice feedback={feedbackFor(`modify-${bot.exchangeId}`)} />
      </div>
    )
  }

  return (
    <div>
      <div className="portal-page-title">
        <h1>Admin — user bot status</h1>
      </div>
      <div className="portal-card">
        <EmailLookupForm email={email} onSubmit={setEmail} />
        <AdminNotice feedback={loadFeedback} />
        {!email && <p className="portal-text-muted">Enter a user e-mail to look up their bots.</p>}
        {email && status === null && !loadFeedback && <p>Loading…</p>}
        {email && status !== null && status.bots.length === 0 && <p className="portal-text-muted">No exchanges configured for this user.</p>}
      </div>
      {email && status !== null && status.bots.map(renderBot)}
    </div>
  )
}
